/**
 * "How does this price compare?" — the registered-market card for one listing.
 *
 * Reads Ministry of Justice sale deeds for the listing's district and shows
 * where its asking price sits. It reports a RANGE and a position, never a value:
 * property valuation is a licensed activity in Saudi Arabia, and the deed data
 * could not support a per-property figure even if it were permitted (no
 * bedrooms, no age, no condition in the source).
 *
 * The three positions are deliberately neutral — "within the usual range",
 * "above most registered deals", "below most registered deals". Never
 * "overpriced", never "a bargain". The colour stays brand green in all three
 * states for the same reason: red here would read as a verdict.
 */

import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/propertyUtils';
import type { MarketCard } from '../../types/marketData';
import MarketRail from './MarketRail';
import MarketEmpty from './MarketEmpty';
import MarketSourceNote from './MarketSourceNote';

export interface MarketPriceCardProps {
  card: MarketCard | null;
  loading?: boolean;
  /** Off on district pages, where there is no listing to place on the rail. */
  showListing?: boolean;
  className?: string;
}

function LoadingState() {
  const { t } = useTranslation('marketData');
  return (
    <div className="animate-pulse space-y-4" aria-busy="true" aria-live="polite">
      <div className="h-4 w-2/5 rounded bg-[var(--pv-color-surface-sunken)]" />
      <div className="h-3 w-full rounded-full bg-[var(--pv-color-surface-sunken)]" />
      <div className="h-3 w-3/5 rounded bg-[var(--pv-color-surface-sunken)]" />
      <span className="sr-only">{t('loading', { defaultValue: 'Loading market data…' })}</span>
    </div>
  );
}

export default function MarketPriceCard({
  card,
  loading = false,
  showListing = true,
  className = '',
}: MarketPriceCardProps) {
  const { t } = useTranslation('marketData');

  const shell = (children: React.ReactNode) => (
    // A block inside the property page's «السوق في هذا الحي» section (no box of its own).
    <section className={className}>
      {children}
    </section>
  );

  if (loading) return shell(<LoadingState />);
  if (!card) return null;
  if (!card.available) return shell(<MarketEmpty kind={card.reason} />);

  const { place, band, segment } = card;

  const header = (
    <header className="mb-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-[var(--pv-color-text)] sm:text-lg">
          {t('title', { defaultValue: 'How does this price compare?' })}
        </h3>
        {place.provisional && (
          <span
            title={t('provisionalNote', { defaultValue: '' })}
            className="rounded-full border border-[#FBE5A3] bg-[var(--pv-color-gold-soft)] px-2 py-0.5 text-[11px] font-semibold text-[#8C6A0B]"
          >
            {t('provisional', { defaultValue: 'Provisional' })}
          </span>
        )}
      </div>
      <p className="mt-1 text-[13px] text-[var(--pv-color-text-muted)]">
        {t('subtitle', {
          district: place.districtAr,
          defaultValue: 'Against sale deeds actually registered in {{district}}',
        })}
      </p>
    </header>
  );

  // Villas, floors and buildings are filed under "land" by the MoJ, so there is
  // no honest sale band for them. Say that, rather than show a land price.
  if (!segment) {
    return shell(
      <>
        {header}
        <MarketEmpty kind="no-segment" />
        <MarketSourceNote sale updatedAt={card.generatedAt} className="mt-5" />
      </>,
    );
  }

  if (!band) {
    return shell(
      <>
        {header}
        <MarketEmpty kind="not-enough-deals" />
        <MarketSourceNote sale updatedAt={card.generatedAt} className="mt-5" />
      </>,
    );
  }

  const positionKey = band.position ?? null;

  return shell(
    <>
      {header}

      {/* The one line a broker will read out loud. */}
      <div className="mb-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-[13px] text-[var(--pv-color-text-muted)]">
          {t('band.typical', { defaultValue: 'Typical price' })}
        </span>
        <span className="text-2xl font-semibold tabular-nums text-[var(--pv-color-text)]">
          {formatNumber(band.median)}
        </span>
        <span className="text-[13px] text-[var(--pv-color-text-muted)]">
          {t('band.perM2', { defaultValue: 'SAR / m²' })}
        </span>
      </div>

      <MarketRail band={band} showListing={showListing} />

      <p className="mt-4 text-[13px] text-[var(--pv-color-text-muted)]">
        {t('band.halfOfDeals', {
          low: formatNumber(band.lo50),
          high: formatNumber(band.hi50),
          defaultValue: 'Half of deals fell between {{low}} and {{high}}',
        })}
      </p>

      {showListing && positionKey && (
        <div className="mt-4 rounded-brand-md border border-[var(--pv-border-subtle)] bg-[var(--pv-color-surface-soft)] p-4">
          <p className="text-sm font-semibold text-[var(--pv-color-primary)]">
            {t(`position.${positionKey}`, { defaultValue: positionKey })}
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--pv-color-text-muted)]">
            {t(`position.${positionKey}Note`, { defaultValue: '' })}
          </p>
        </div>
      )}

      {showListing && !positionKey && (
        <p className="mt-4 text-[13px] text-[var(--pv-color-text-subtle)]">
          {t('position.noArea', { defaultValue: 'Add the property area to compare price per m².' })}
        </p>
      )}

      {/* Sample size and period are part of the claim, not small print. */}
      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[var(--pv-border-subtle)] pt-4 text-[13px] sm:grid-cols-3">
        <div>
          <dt className="text-[var(--pv-color-text-subtle)]">
            {band.window === '12m'
              ? t('band.window12', { defaultValue: 'Last 12 months' })
              : t('band.window24', { defaultValue: 'Last 24 months' })}
          </dt>
          <dd className="mt-0.5 font-semibold tabular-nums text-[var(--pv-color-text)]">
            {t('band.deeds', { count: band.n, defaultValue: '{{count}} registered deals' })}
          </dd>
        </div>
        <div>
          <dt className="text-[var(--pv-color-text-subtle)]">
            {t('comps.date', { defaultValue: 'Date' })}
          </dt>
          <dd className="mt-0.5 font-semibold tabular-nums text-[var(--pv-color-text)]" dir="ltr">
            {band.from} – {band.to}
          </dd>
        </div>
        {card.activity?.deeds_last4q ? (
          <div>
            <dt className="text-[var(--pv-color-text-subtle)]">
              {t('activity.title', { defaultValue: 'Market activity' })}
            </dt>
            <dd className="mt-0.5 font-semibold tabular-nums text-[var(--pv-color-text)]">
              {formatNumber(card.activity.deeds_last4q)}
            </dd>
          </div>
        ) : null}
      </dl>

      {place.provisional && (
        <p className="mt-3 rounded-brand-sm border border-[#FBE5A3] bg-[var(--pv-color-gold-soft)] p-3 text-[12px] leading-relaxed text-[#8C6A0B]">
          {t('provisionalNote', { defaultValue: '' })}
        </p>
      )}

      <MarketSourceNote sale updatedAt={card.generatedAt} className="mt-5" />
    </>,
  );
}
